import React, { useEffect, useState } from "react";
import { Plus, Pencil, Boxes, MapPin, Crosshair } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { StockTable } from "./shared";
import { StockAdjustDialog } from "./StockForms";

const empty = { name: "", code: "", address: "", phone: "", lat: "", lng: "", active: true };

const StationDialog = ({ editing, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? { ...empty, ...editing, lat: editing.lat ?? "", lng: editing.lng ?? "" } : empty);
  }, [open, editing]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { name: form.name.trim(), code: form.code, address: form.address, phone: form.phone, active: form.active,
      lat: form.lat === "" ? null : Number(form.lat), lng: form.lng === "" ? null : Number(form.lng) };
    if (!body.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/stations/${editing.id}`, body); else await api.post("/stations", body);
      toast.success(editing ? "Station saved" : "Station created");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="station-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "New Stabex station"}</DialogTitle>
          <DialogDescription>GPS coordinates map households to their nearest station.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} className="rounded-sm mt-1" data-testid="station-name" /></div>
            <div><Label>Code</Label><Input value={form.code} onChange={set("code")} className="rounded-sm mt-1" data-testid="station-code" /></div>
          </div>
          <div><Label>Address</Label><Input value={form.address} onChange={set("address")} className="rounded-sm mt-1" data-testid="station-address" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} className="rounded-sm mt-1" data-testid="station-phone" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Latitude</Label><Input type="number" step="any" value={form.lat} onChange={set("lat")} className="rounded-sm mt-1" data-testid="station-lat" /></div>
            <div><Label>Longitude</Label><Input type="number" step="any" value={form.lng} onChange={set("lng")} className="rounded-sm mt-1" data-testid="station-lng" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={set("active")} data-testid="station-active" /> Active</label>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-sm bg-brand-600 hover:bg-brand-700" data-testid="station-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StationCard = ({ s, i, onEdit, onStock }) => (
  <div className={`bg-white border rounded-sm p-5 ${s.active ? "border-slate-200" : "border-dashed border-slate-300 opacity-70"}`} data-testid={`station-card-${i}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-heading font-bold text-slate-900">{s.name} {s.code && <span className="text-xs font-mono text-slate-400 ml-1">{s.code}</span>}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={11} />{s.address || "—"}{s.lat != null && ` · ${s.lat.toFixed(4)}, ${s.lng.toFixed(4)}`}</div>
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => onStock(s)} data-testid={`station-stock-btn-${i}`}><Boxes size={12} className="mr-1" />Stock</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => onEdit(s)} data-testid={`station-edit-btn-${i}`}><Pencil size={12} /></Button>
      </div>
    </div>
    <div className="mt-3"><StockTable rows={s.stock_rows} testId={`station-stock-${i}`} /></div>
    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
      <div><span className="font-semibold text-slate-900 tabular-nums">{s.household_count}</span> households</div>
      <div><span className="font-semibold text-slate-900 tabular-nums">{s.open_orders}</span> open orders</div>
      <div><span className="font-semibold text-slate-900 tabular-nums">{s.empties_due}</span> empties due</div>
    </div>
  </div>
);

export const StationsPanel = ({ stations, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [stockFor, setStockFor] = useState(null);
  const assign = async () => {
    try { const { data } = await api.post("/stations/assign-nearest"); toast.success(`${data.assigned} household(s) mapped to their nearest station`); reload(); }
    catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="stations-panel">
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" className="rounded-sm" onClick={assign} data-testid="stations-assign-nearest"><Crosshair size={14} className="mr-1" />Map households to nearest</Button>
        <Button size="sm" className="rounded-sm bg-brand-600 hover:bg-brand-700" onClick={() => { setEditing(null); setOpen(true); }} data-testid="stations-add-btn"><Plus size={14} className="mr-1" />Add station</Button>
      </div>
      {stations.length === 0 && <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center text-sm text-slate-400">No stations yet — add the participating Stabex stations.</div>}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {stations.map((s, i) => <StationCard key={s.id} s={s} i={i} onEdit={(x) => { setEditing(x); setOpen(true); }} onStock={setStockFor} />)}
      </div>
      <StationDialog editing={editing} open={open} onOpenChange={setOpen} onSaved={reload} />
      <StockAdjustDialog station={stockFor} open={!!stockFor} onOpenChange={(v) => !v && setStockFor(null)} onSaved={reload} />
    </div>
  );
};
